'use client'

import ActionsMenu from '@/components/actions-menu'
import { ChatChannelIcon, chatChannelMeta } from '@/components/atendimento/chat-channel-icon'
import SalesTasksPanel from '@/components/sales/SalesTasksPanel'
import AtendimentoChatPage from '@/pages/admin/atendimento/atendimentos/atendimento-chat-page'
import MoveDealPipelineModal from '@/pages/admin/sales/pipelines/move-deal-pipeline-modal'
import { DEAL_STATUS_COLORS, DEAL_STATUS_LABELS } from '@/lib/api/sales/deals-api'
import {
    assignLead,
    AssignLeadData,
    convertLead,
    ConvertLeadData,
    getLead,
    getLeadSentiments,
    getLeadTimeline,
    Lead,
    LEAD_LIFECYCLE_LABELS,
    LEAD_SENTIMENT_LABELS,
    LEAD_SOURCE_LABELS,
    LEAD_STATUS_COLORS,
    LEAD_STATUS_LABELS,
    LEAD_TEMPERATURE_COLORS,
    LEAD_TEMPERATURE_LABELS,
    LeadActivity,
    LeadLifecycleStatus,
    LeadSentiment,
    LeadSentimentType,
    LeadSource,
    LeadStatus,
    LeadTemperature,
    updateLead,
    UpdateLeadData,
} from '@/lib/api/sales/leads-api'
import { getWallets, SalesWallet } from '@/lib/api/sales/wallets-api'
import { UserSelector } from '@/components/UserSelector'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useMemo, useState } from 'react'
import {
    MdAcUnit,
    MdAdd,
    MdArrowBack,
    MdAssignment,
    MdBusiness,
    MdCall,
    MdCancel,
    MdChat,
    MdCheckCircle,
    MdDriveFileMove,
    MdEdit,
    MdEmail,
    MdForum,
    MdHandshake,
    MdLocalOffer,
    MdNote,
    MdOpenInNew,
    MdPerson,
    MdPersonAdd,
    MdPhone,
    MdSentimentSatisfied,
    MdSwapVert,
    MdTimeline,
    MdTrendingUp,
    MdWarning,
    MdWhatshot,
    MdWork
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

type TabKey = 'summary' | 'deals' | 'timeline' | 'sentiment' | 'chats' | 'tasks'

const SENTIMENT_COLORS: Record<LeadSentimentType, string> = {
    INTERESTED: 'badge-info',
    VERY_SATISFIED: 'badge-success',
    SATISFIED: 'badge-success',
    NEUTRAL: 'badge-ghost',
    CURIOUS: 'badge-info',
    HESITANT: 'badge-warning',
    DISSATISFIED: 'badge-warning',
    FRUSTRATED: 'badge-error',
    ANGRY: 'badge-error',
    URGENT: 'badge-error',
}

const ACTIVITY_TYPE_ICONS: Record<string, React.ReactNode> = {
    STAGE_CHANGE: <MdSwapVert className="text-info" />,
    SENTIMENT_CHANGE: <MdSentimentSatisfied className="text-warning" />,
    MESSAGE: <MdChat className="text-secondary" />,
    NOTE: <MdNote className="text-secondary" />,
    CALL: <MdCall className="text-success" />,
    EMAIL: <MdEmail className="text-info" />,
    SCORE_CHANGE: <MdTrendingUp className="text-accent" />,
    ASSIGNED: <MdPersonAdd className="text-secondary" />,
    DEAL_CREATED: <MdHandshake className="text-success" />,
    DEAL_WON: <MdCheckCircle className="text-success" />,
    DEAL_LOST: <MdCancel className="text-error" />,
    SLA_BREACH: <MdWarning className="text-error" />,
}

interface LeadDetailPageProps {
    idOverride?: number
    onBack?: () => void
}

export default function LeadDetailPage({ idOverride, onBack }: LeadDetailPageProps = {}) {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id: paramId } = useParams<{ id: string }>()
    const id = idOverride ? String(idOverride) : paramId
    /** No modal, sair da lista significa fechar; na rota, navegar. */
    const goBack = () => (onBack ? onBack() : navigate('/sales/leads'))
    const { canUpdate } = useResourcePermissions('sales', 'leads')
    const { canUpdate: canUpdateDeals } = useResourcePermissions('sales', 'deals')

    const [lead, setLead] = useState<Lead | null>(null)
    const [timeline, setTimeline] = useState<LeadActivity[]>([])
    const [sentiments, setSentiments] = useState<LeadSentiment[]>([])
    const [wallets, setWallets] = useState<SalesWallet[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<TabKey>('summary')

    // Mover oportunidade para outro funil (mesmo modal do kanban)
    const [movePipelineDeal, setMovePipelineDeal] = useState<{ id: number; title: string; pipelineId: number } | null>(null)
    // Conversa aberta na aba "Conversas"; vazio = a mais recente
    const [selectedChatId, setSelectedChatId] = useState<number | null>(null)

    // Edit dialog
    const [showEditModal, setShowEditModal] = useState(false)
    const [editForm, setEditForm] = useState<UpdateLeadData>({})
    const [saving, setSaving] = useState(false)

    // Convert dialog
    const [showConvertModal, setShowConvertModal] = useState(false)
    const [convertForm, setConvertForm] = useState<ConvertLeadData>({})
    const [converting, setConverting] = useState(false)

    // Assign dialog
    const [showAssignModal, setShowAssignModal] = useState(false)
    const [assignForm, setAssignForm] = useState<AssignLeadData>({})
    const [assigning, setAssigning] = useState(false)

    useEffect(() => {
        if (id) {
            loadLead()
            loadWallets()
        }
    }, [id])

    useEffect(() => {
        if (id && activeTab === 'timeline') loadTimeline()
        if (id && activeTab === 'sentiment') loadSentiments()
    }, [id, activeTab])

    const loadLead = async () => {
        if (!id) return
        setLoading(true)
        try {
            const response = await getLead(parseInt(id, 10))
            if (response.success && response.result) {
                setLead(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar lead')
                goBack()
            }
        } catch {
            toast.error('Erro ao carregar lead')
            goBack()
        } finally {
            setLoading(false)
        }
    }

    const loadTimeline = async () => {
        if (!id) return
        try {
            const response = await getLeadTimeline(parseInt(id, 10))
            if (response.success && response.result) {
                setTimeline(response.result)
            }
        } catch {
            toast.error('Erro ao carregar timeline')
        }
    }

    const loadSentiments = async () => {
        if (!id) return
        try {
            const response = await getLeadSentiments(parseInt(id, 10))
            if (response.success && response.result) {
                setSentiments(response.result)
            }
        } catch {
            toast.error('Erro ao carregar sentimentos')
        }
    }

    const loadWallets = async () => {
        try {
            const response = await getWallets()
            if (response.success && response.result) {
                setWallets(response.result)
            }
        } catch {
            // Silently fail
        }
    }

    const openEditModal = () => {
        if (!lead) return
        setEditForm({
            name: lead.name,
            phoneNumber: lead.phoneNumber,
            email: lead.email || '',
            source: lead.source,
            status: lead.status,
            lifecycleStatus: lead.lifecycleStatus,
            temperature: lead.temperature,
            isMQL: lead.isMQL,
            isSAL: lead.isSAL,
            isSQL: lead.isSQL,
            hasBudget: lead.hasBudget,
            isDecisionMaker: lead.isDecisionMaker,
            hasNeed: lead.hasNeed,
            expectedTimeline: lead.expectedTimeline || '',
            qualificationNotes: lead.qualificationNotes || '',
            utmSource: lead.utmSource || '',
            utmMedium: lead.utmMedium || '',
            utmCampaign: lead.utmCampaign || '',
            utmTerm: lead.utmTerm || '',
            utmContent: lead.utmContent || '',
            leadCompanyName: lead.leadCompanyName || '',
            position: lead.position || '',
            leadWebsite: lead.leadWebsite || '',
            tags: lead.tags || [],
            notes: lead.notes || '',
        })
        setShowEditModal(true)
    }

    const handleSaveEdit = async () => {
        if (!id) return
        setSaving(true)
        try {
            const response = await updateLead(parseInt(id, 10), editForm)
            if (response.success) {
                toast.success('Lead atualizado com sucesso')
                setShowEditModal(false)
                loadLead()
            } else {
                toast.error(response.error || 'Erro ao atualizar')
            }
        } catch {
            toast.error('Erro ao atualizar lead')
        } finally {
            setSaving(false)
        }
    }

    const handleConvert = async () => {
        if (!id) return
        setConverting(true)
        try {
            const response = await convertLead(parseInt(id, 10), convertForm)
            if (response.success) {
                toast.success('Lead convertido em cliente com sucesso')
                setShowConvertModal(false)
                loadLead()
            } else {
                toast.error(response.error || 'Erro ao converter')
            }
        } catch {
            toast.error('Erro ao converter lead')
        } finally {
            setConverting(false)
        }
    }

    const handleAssign = async () => {
        if (!id) return
        setAssigning(true)
        try {
            const response = await assignLead(parseInt(id, 10), assignForm)
            if (response.success) {
                toast.success('Lead atribuído com sucesso')
                setShowAssignModal(false)
                loadLead()
            } else {
                toast.error(response.error || 'Erro ao atribuir')
            }
        } catch {
            toast.error('Erro ao atribuir lead')
        } finally {
            setAssigning(false)
        }
    }

    const scoreColor = (score: number) => {
        if (score >= 80) return 'text-success'
        if (score >= 50) return 'text-warning'
        return 'text-error'
    }

    const formatBRL = (value: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

    if (loading) {
        return (
            <div className="p-6 space-y-4">
                <div className="skeleton h-8 w-48" />
                <div className="skeleton h-32 w-full" />
                <div className="skeleton h-64 w-full" />
            </div>
        )
    }

    if (!lead) {
        return (
            <div className="p-6">
                <div className="alert alert-error">Lead não encontrado</div>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex items-start gap-4">
                    <button
                        className="btn btn-ghost btn-sm mt-1"
                        onClick={() => goBack()}
                    >
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl font-bold">{lead.name}</h1>
                            {lead.currentSentiment && (
                                <span className={`badge ${SENTIMENT_COLORS[lead.currentSentiment]}`}>
                                    {LEAD_SENTIMENT_LABELS[lead.currentSentiment]}
                                </span>
                            )}
                            <span className={`badge ${LEAD_TEMPERATURE_COLORS[lead.temperature]}`}>
                                {lead.temperature === 'HOT' && <MdWhatshot size={14} className="mr-1" />}
                                {lead.temperature === 'FROZEN' && <MdAcUnit size={14} className="mr-1" />}
                                {LEAD_TEMPERATURE_LABELS[lead.temperature]}
                            </span>
                            <span className={`badge ${LEAD_STATUS_COLORS[lead.status]}`}>
                                {LEAD_STATUS_LABELS[lead.status]}
                            </span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm opacity-70 flex-wrap">
                            {lead.phoneNumber && (
                                <span className="flex items-center gap-1">
                                    <MdPhone size={14} /> {lead.phoneNumber}
                                </span>
                            )}
                            {/* Canais em que o lead conversa (Instagram e Messenger não têm telefone) */}
                            {Array.from(new Set((lead.chats ?? []).map(c => c.channel || 'WHATSAPP'))).map(channel => (
                                <span key={channel} className="flex items-center gap-1">
                                    <ChatChannelIcon channel={channel} /> {chatChannelMeta(channel).label}
                                </span>
                            ))}
                            {lead.email && (
                                <span className="flex items-center gap-1">
                                    <MdEmail size={14} /> {lead.email}
                                </span>
                            )}
                            {lead.assignedUser && (
                                <span className="flex items-center gap-1">
                                    <MdPerson size={14} /> {lead.assignedUser.name}
                                </span>
                            )}
                        </div>
                        {/* Score + Lifecycle + BANT inline */}
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                            {/* Score */}
                            <div className="flex items-center gap-2">
                                <div className={`radial-progress ${scoreColor(lead.score || 0)} text-xs font-bold`}
                                    style={{ '--value': lead.score || 0, '--size': '3rem', '--thickness': '4px' } as any}
                                    role="progressbar"
                                >
                                    {lead.score || 0}
                                </div>
                                <span className="text-xs opacity-50">Score</span>
                            </div>

                            <div className="divider divider-horizontal mx-0" />

                            {/* Lifecycle - edição rápida */}
                            {canUpdate ? (
                                <select
                                    className="select select-bordered select-xs"
                                    value={lead.lifecycleStatus}
                                    onChange={async (e) => {
                                        const value = e.target.value as LeadLifecycleStatus
                                        try {
                                            const res = await updateLead(lead.id, { lifecycleStatus: value })
                                            if (res.success) {
                                                setLead({ ...lead, lifecycleStatus: value })
                                                toast.success('Ciclo de vida atualizado')
                                            }
                                        } catch { toast.error('Erro ao atualizar') }
                                    }}
                                >
                                    {Object.entries(LEAD_LIFECYCLE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            ) : (
                                <span className="badge badge-outline">{LEAD_LIFECYCLE_LABELS[lead.lifecycleStatus]}</span>
                            )}

                            {/* MQL/SAL/SQL inline toggles */}
                            {canUpdate ? (
                                <>
                                    <label className="flex items-center gap-1 cursor-pointer" title="Marketing Qualified Lead">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-xs checkbox-info"
                                            checked={lead.isMQL || false}
                                            onChange={async (e) => {
                                                const val = e.target.checked
                                                try {
                                                    const res = await updateLead(lead.id, { isMQL: val })
                                                    if (res.success) { setLead({ ...lead, isMQL: val }); toast.success('Atualizado') }
                                                } catch { toast.error('Erro') }
                                            }}
                                        />
                                        <span className="text-xs">MQL</span>
                                    </label>
                                    <label className="flex items-center gap-1 cursor-pointer" title="Sales Accepted Lead">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-xs checkbox-warning"
                                            checked={lead.isSAL || false}
                                            onChange={async (e) => {
                                                const val = e.target.checked
                                                try {
                                                    const res = await updateLead(lead.id, { isSAL: val })
                                                    if (res.success) { setLead({ ...lead, isSAL: val }); toast.success('Atualizado') }
                                                } catch { toast.error('Erro') }
                                            }}
                                        />
                                        <span className="text-xs">SAL</span>
                                    </label>
                                    <label className="flex items-center gap-1 cursor-pointer" title="Sales Qualified Lead">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-xs checkbox-success"
                                            checked={lead.isSQL || false}
                                            onChange={async (e) => {
                                                const val = e.target.checked
                                                try {
                                                    const res = await updateLead(lead.id, { isSQL: val })
                                                    if (res.success) { setLead({ ...lead, isSQL: val }); toast.success('Atualizado') }
                                                } catch { toast.error('Erro') }
                                            }}
                                        />
                                        <span className="text-xs">SQL</span>
                                    </label>
                                </>
                            ) : (
                                <>
                                    {lead.isMQL && <span className="badge badge-xs badge-info">MQL</span>}
                                    {lead.isSAL && <span className="badge badge-xs badge-warning">SAL</span>}
                                    {lead.isSQL && <span className="badge badge-xs badge-success">SQL</span>}
                                </>
                            )}

                            <div className="divider divider-horizontal mx-0" />

                            {/* BANT inline toggles */}
                            <div className="flex items-center gap-2">
                                <span className="text-xs opacity-50">BANT:</span>
                                {canUpdate ? (
                                    <>
                                        <label className="flex items-center gap-1 cursor-pointer" title="Budget (Orçamento)">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-xs checkbox-success"
                                                checked={lead.hasBudget || false}
                                                onChange={async (e) => {
                                                    const val = e.target.checked
                                                    try {
                                                        const res = await updateLead(lead.id, { hasBudget: val })
                                                        if (res.success) { setLead({ ...lead, hasBudget: val }); toast.success('Atualizado') }
                                                    } catch { toast.error('Erro') }
                                                }}
                                            />
                                            <span className="text-xs">B</span>
                                        </label>
                                        <label className="flex items-center gap-1 cursor-pointer" title="Authority (Autoridade)">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-xs checkbox-success"
                                                checked={lead.isDecisionMaker || false}
                                                onChange={async (e) => {
                                                    const val = e.target.checked
                                                    try {
                                                        const res = await updateLead(lead.id, { isDecisionMaker: val })
                                                        if (res.success) { setLead({ ...lead, isDecisionMaker: val }); toast.success('Atualizado') }
                                                    } catch { toast.error('Erro') }
                                                }}
                                            />
                                            <span className="text-xs">A</span>
                                        </label>
                                        <label className="flex items-center gap-1 cursor-pointer" title="Need (Necessidade)">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-xs checkbox-success"
                                                checked={lead.hasNeed || false}
                                                onChange={async (e) => {
                                                    const val = e.target.checked
                                                    try {
                                                        const res = await updateLead(lead.id, { hasNeed: val })
                                                        if (res.success) { setLead({ ...lead, hasNeed: val }); toast.success('Atualizado') }
                                                    } catch { toast.error('Erro') }
                                                }}
                                            />
                                            <span className="text-xs">N</span>
                                        </label>
                                        <span className="text-xs opacity-40" title="Timeline (Prazo)">T: {lead.expectedTimeline || '-'}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className={`badge badge-xs ${lead.hasBudget ? 'badge-success' : 'badge-ghost'}`}>B</span>
                                        <span className={`badge badge-xs ${lead.isDecisionMaker ? 'badge-success' : 'badge-ghost'}`}>A</span>
                                        <span className={`badge badge-xs ${lead.hasNeed ? 'badge-success' : 'badge-ghost'}`}>N</span>
                                        <span className="text-xs opacity-40">T: {lead.expectedTimeline || '-'}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {canUpdate && (
                    <div className="flex gap-2 flex-wrap">
                        <button className="btn btn-sm btn-outline" onClick={openEditModal}>
                            <MdEdit size={16} /> Editar
                        </button>
                        {lead.status !== 'CONVERTED' && (
                            <button
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                    setConvertForm({ name: lead.name, email: lead.email })
                                    setShowConvertModal(true)
                                }}
                            >
                                <MdCheckCircle size={16} /> Converter em Cliente
                            </button>
                        )}
                        <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => {
                                setAssignForm({
                                    assignedUserId: lead.assignedUserId,
                                })
                                setShowAssignModal(true)
                            }}
                        >
                            <MdPersonAdd size={16} /> Atribuir
                        </button>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div role="tablist" className="tabs tabs-bordered">
                {([
                    ['summary', 'Resumo'],
                    ['deals', 'Negócios'],
                    ['tasks', 'Tarefas'],
                    ['timeline', 'Timeline'],
                    ['sentiment', 'Sentimento'],
                    ['chats', 'Conversas'],
                ] as [TabKey, string][]).map(([key, label]) => (
                    <button
                        key={key}
                        role="tab"
                        className={`tab ${activeTab === key ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'summary' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Dados Básicos */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title text-base">
                                <MdPerson size={18} /> Dados Básicos
                            </h3>
                            <div className="space-y-3 mt-2">
                                <InfoRow label="Nome" value={lead.name} />
                                <InfoRow label="Telefone" value={lead.phoneNumber} />
                                <InfoRow label="E-mail" value={lead.email || '-'} />
                                <InfoRow
                                    label="Origem"
                                    value={
                                        lead.source === 'INSTAGRAM' || lead.source === 'MESSENGER' || lead.source === 'WHATSAPP' || lead.source === 'DEX_TALK' ? (
                                            <span className="inline-flex items-center gap-1.5">
                                                <ChatChannelIcon channel={lead.source} />
                                                {LEAD_SOURCE_LABELS[lead.source]}
                                            </span>
                                        ) : (
                                            LEAD_SOURCE_LABELS[lead.source]
                                        )
                                    }
                                />
                                <InfoRow label="Status" value={LEAD_STATUS_LABELS[lead.status]} />
                                <InfoRow label="Ciclo de Vida" value={LEAD_LIFECYCLE_LABELS[lead.lifecycleStatus]} />
                                <InfoRow label="Temperatura" value={LEAD_TEMPERATURE_LABELS[lead.temperature]} />
                                {lead.notes && <InfoRow label="Observações" value={lead.notes} />}
                                <InfoRow
                                    label="Criado em"
                                    value={format(new Date(lead.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                />
                                {lead.lastContactAt && (
                                    <InfoRow
                                        label="Último Contato"
                                        value={format(new Date(lead.lastContactAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Qualificação BANT + Lifecycle */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title text-base">
                                <MdAssignment size={18} /> Qualificação
                            </h3>
                            <div className="space-y-3 mt-2">
                                {/* Lifecycle */}
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">Ciclo de Vida</span>
                                    {canUpdate ? (
                                        <select
                                            className="select select-bordered select-xs"
                                            value={lead.lifecycleStatus}
                                            onChange={async (e) => {
                                                const value = e.target.value as LeadLifecycleStatus
                                                try {
                                                    const res = await updateLead(lead.id, { lifecycleStatus: value })
                                                    if (res.success) { setLead({ ...lead, lifecycleStatus: value }); toast.success('Ciclo de vida atualizado') }
                                                } catch { toast.error('Erro ao atualizar') }
                                            }}
                                        >
                                            {Object.entries(LEAD_LIFECYCLE_LABELS).map(([k, v]) => (
                                                <option key={k} value={k}>{v}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="badge badge-outline badge-sm">{LEAD_LIFECYCLE_LABELS[lead.lifecycleStatus]}</span>
                                    )}
                                </div>

                                <div className="divider my-1 text-xs opacity-40">MQL / SAL / SQL</div>

                                {/* MQL */}
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">MQL (Marketing Qualified)</span>
                                    {canUpdate ? (
                                        <input type="checkbox" className="toggle toggle-sm toggle-info" checked={lead.isMQL || false}
                                            onChange={async (e) => { const val = e.target.checked; try { const res = await updateLead(lead.id, { isMQL: val }); if (res.success) { setLead({ ...lead, isMQL: val }); toast.success('Atualizado') } } catch { toast.error('Erro') } }} />
                                    ) : <span className={`badge badge-sm ${lead.isMQL ? 'badge-info' : 'badge-ghost'}`}>{lead.isMQL ? 'Sim' : 'Não'}</span>}
                                </div>
                                {/* SAL */}
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">SAL (Sales Accepted)</span>
                                    {canUpdate ? (
                                        <input type="checkbox" className="toggle toggle-sm toggle-warning" checked={lead.isSAL || false}
                                            onChange={async (e) => { const val = e.target.checked; try { const res = await updateLead(lead.id, { isSAL: val }); if (res.success) { setLead({ ...lead, isSAL: val }); toast.success('Atualizado') } } catch { toast.error('Erro') } }} />
                                    ) : <span className={`badge badge-sm ${lead.isSAL ? 'badge-warning' : 'badge-ghost'}`}>{lead.isSAL ? 'Sim' : 'Não'}</span>}
                                </div>
                                {/* SQL */}
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">SQL (Sales Qualified)</span>
                                    {canUpdate ? (
                                        <input type="checkbox" className="toggle toggle-sm toggle-success" checked={lead.isSQL || false}
                                            onChange={async (e) => { const val = e.target.checked; try { const res = await updateLead(lead.id, { isSQL: val }); if (res.success) { setLead({ ...lead, isSQL: val }); toast.success('Atualizado') } } catch { toast.error('Erro') } }} />
                                    ) : <span className={`badge badge-sm ${lead.isSQL ? 'badge-success' : 'badge-ghost'}`}>{lead.isSQL ? 'Sim' : 'Não'}</span>}
                                </div>

                                <div className="divider my-1 text-xs opacity-40">BANT</div>

                                {/* BANT com toggles */}
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">Orçamento (Budget)</span>
                                    {canUpdate ? (
                                        <input type="checkbox" className="toggle toggle-sm toggle-success" checked={lead.hasBudget || false}
                                            onChange={async (e) => { const val = e.target.checked; try { const res = await updateLead(lead.id, { hasBudget: val }); if (res.success) { setLead({ ...lead, hasBudget: val }); toast.success('Atualizado') } } catch { toast.error('Erro') } }} />
                                    ) : <BANTItem label="" checked={lead.hasBudget} />}
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">Autoridade (Authority)</span>
                                    {canUpdate ? (
                                        <input type="checkbox" className="toggle toggle-sm toggle-success" checked={lead.isDecisionMaker || false}
                                            onChange={async (e) => { const val = e.target.checked; try { const res = await updateLead(lead.id, { isDecisionMaker: val }); if (res.success) { setLead({ ...lead, isDecisionMaker: val }); toast.success('Atualizado') } } catch { toast.error('Erro') } }} />
                                    ) : <BANTItem label="" checked={lead.isDecisionMaker} />}
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">Necessidade (Need)</span>
                                    {canUpdate ? (
                                        <input type="checkbox" className="toggle toggle-sm toggle-success" checked={lead.hasNeed || false}
                                            onChange={async (e) => { const val = e.target.checked; try { const res = await updateLead(lead.id, { hasNeed: val }); if (res.success) { setLead({ ...lead, hasNeed: val }); toast.success('Atualizado') } } catch { toast.error('Erro') } }} />
                                    ) : <BANTItem label="" checked={lead.hasNeed} />}
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">Prazo (Timeline)</span>
                                    <span className="text-sm font-medium">{lead.expectedTimeline || 'Não definido'}</span>
                                </div>
                                {lead.qualificationNotes && (
                                    <div className="mt-2 p-2 bg-base-200 rounded text-sm">
                                        {lead.qualificationNotes}
                                    </div>
                                )}

                                {/* Score detalhado */}
                                <div className="divider my-1 text-xs opacity-40">Score</div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm opacity-70">Score Total</span>
                                    <span className={`text-lg font-bold ${scoreColor(lead.score || 0)}`}>{lead.score || 0}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* UTM */}
                    {(lead.utmSource || lead.utmMedium || lead.utmCampaign) && (
                        <div className="card bg-base-100 shadow-sm">
                            <div className="card-body">
                                <h3 className="card-title text-base">
                                    <MdTrendingUp size={18} /> UTM
                                </h3>
                                <div className="space-y-3 mt-2">
                                    {lead.utmSource && <InfoRow label="Source" value={lead.utmSource} />}
                                    {lead.utmMedium && <InfoRow label="Medium" value={lead.utmMedium} />}
                                    {lead.utmCampaign && <InfoRow label="Campaign" value={lead.utmCampaign} />}
                                    {lead.utmTerm && <InfoRow label="Term" value={lead.utmTerm} />}
                                    {lead.utmContent && <InfoRow label="Content" value={lead.utmContent} />}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Extras */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title text-base">
                                <MdBusiness size={18} /> Informações Extras
                            </h3>
                            <div className="space-y-3 mt-2">
                                <InfoRow label="Empresa" value={lead.leadCompanyName || '-'} />
                                <InfoRow label="Cargo" value={lead.position || '-'} />
                                <InfoRow label="Website" value={lead.leadWebsite || '-'} />
                                {lead.tags && lead.tags.length > 0 && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm opacity-70">Tags</span>
                                        <div className="flex gap-1 flex-wrap">
                                            {lead.tags.map((tag, i) => (
                                                <span key={i} className="badge badge-sm badge-outline">
                                                    <MdLocalOffer size={10} className="mr-1" />
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {lead.customFields && Object.keys(lead.customFields).length > 0 && (
                                    <div className="mt-2">
                                        <span className="text-sm opacity-70">Campos Personalizados</span>
                                        <div className="mt-1 p-2 bg-base-200 rounded text-sm">
                                            {Object.entries(lead.customFields).map(([key, val]) => (
                                                <div key={key} className="flex justify-between">
                                                    <span className="opacity-70">{key}:</span>
                                                    <span>{String(val)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Carteira */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title text-base">
                                <MdWork size={18} /> Carteira
                            </h3>
                            {/* A carteira vem do cliente vinculado — lead em
                                triagem ainda não pertence a nenhuma. */}
                            <div className="space-y-3 mt-2">
                                <InfoRow
                                    label="Carteira"
                                    value={
                                        lead.convertedClient?.wallet?.name ||
                                        (lead.convertedClient
                                            ? 'Sem carteira'
                                            : 'Em triagem (sem cliente)')
                                    }
                                />
                                <InfoRow label="Responsável" value={lead.assignedUser?.name || 'Não atribuído'} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'deals' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="font-semibold">Negócios vinculados</h3>
                        <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => navigate(`/sales/deals/novo?leadId=${lead.id}`)}
                        >
                            <MdAdd size={16} /> Nova Oportunidade
                        </button>
                    </div>
                    {!lead.deals || lead.deals.length === 0 ? (
                        <div className="text-center py-12 opacity-50">
                            <MdHandshake size={48} className="mx-auto mb-2" />
                            <p>Nenhuma oportunidade vinculada a este lead</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>Título</th>
                                        <th>Funil / Etapa</th>
                                        <th>Valor</th>
                                        <th>Status</th>
                                        <th>Previsão</th>
                                        <th className="w-px" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {lead.deals.map((deal: any) => (
                                        <tr
                                            key={deal.id}
                                            className="hover cursor-pointer"
                                            onClick={() => navigate(`/sales/deals/${deal.id}`)}
                                        >
                                            <td className="font-medium">{deal.title}</td>
                                            <td>
                                                <span className="text-sm">
                                                    {deal.pipeline?.name} &gt;{' '}
                                                    <span
                                                        className="font-medium"
                                                        style={{ color: deal.stage?.color || undefined }}
                                                    >
                                                        {deal.stage?.name}
                                                    </span>
                                                </span>
                                            </td>
                                            <td>{formatBRL(deal.totalValue || 0)}</td>
                                            <td>
                                                <span className={`badge ${DEAL_STATUS_COLORS[deal.status as keyof typeof DEAL_STATUS_COLORS] || 'badge-ghost'}`}>
                                                    {DEAL_STATUS_LABELS[deal.status as keyof typeof DEAL_STATUS_LABELS] || deal.status}
                                                </span>
                                            </td>
                                            <td>
                                                {deal.expectedCloseDate
                                                    ? format(new Date(deal.expectedCloseDate), 'dd/MM/yyyy', { locale: ptBR })
                                                    : '-'}
                                            </td>
                                            <td onClick={e => e.stopPropagation()}>
                                                <ActionsMenu
                                                    portal
                                                    items={[
                                                        {
                                                            key: 'open',
                                                            label: 'Abrir oportunidade',
                                                            icon: <MdOpenInNew />,
                                                            onClick: () => navigate(`/sales/deals/${deal.id}`),
                                                        },
                                                        ...(canUpdateDeals && deal.status === 'OPEN'
                                                            ? [
                                                                  {
                                                                      key: 'move-pipeline',
                                                                      label: 'Mover para outro funil',
                                                                      icon: <MdDriveFileMove />,
                                                                      onClick: () =>
                                                                          setMovePipelineDeal({
                                                                              id: deal.id,
                                                                              title: deal.title,
                                                                              pipelineId: deal.pipelineId,
                                                                          }),
                                                                  },
                                                              ]
                                                            : []),
                                                    ]}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'tasks' && lead && (
                <SalesTasksPanel leadId={lead.id} defaultAssigneeId={lead.assignedUserId ?? null} />
            )}

            {activeTab === 'timeline' && (
                <div className="space-y-4">
                    {timeline.length === 0 ? (
                        <div className="text-center py-12 opacity-50">
                            <MdTimeline size={48} className="mx-auto mb-2" />
                            <p>Nenhuma atividade registrada</p>
                        </div>
                    ) : (
                        <ul className="timeline timeline-vertical timeline-compact">
                            {timeline.map((activity, index) => (
                                <li key={activity.id}>
                                    {index > 0 && <hr />}
                                    <div className="timeline-start text-xs opacity-60 whitespace-nowrap">
                                        {format(new Date(activity.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                                    </div>
                                    <div className="timeline-middle text-xl">
                                        {ACTIVITY_TYPE_ICONS[activity.type] || <MdNote className="text-base-content" />}
                                    </div>
                                    <div className="timeline-end timeline-box">
                                        <div className="font-medium text-sm">{activity.type.replace(/_/g, ' ')}</div>
                                        <p className="text-sm opacity-70">{activity.description}</p>
                                        {activity.user && (
                                            <span className="text-xs opacity-50">por {activity.user.name}</span>
                                        )}
                                    </div>
                                    {index < timeline.length - 1 && <hr />}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {activeTab === 'sentiment' && (
                <div className="space-y-6">
                    {/* Current sentiment */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title text-base">Sentimento Atual</h3>
                            <div className="flex items-center gap-4 mt-2">
                                <span className={`badge badge-lg ${lead.currentSentiment ? SENTIMENT_COLORS[lead.currentSentiment] : 'badge-ghost'}`}>
                                    {lead.currentSentiment ? LEAD_SENTIMENT_LABELS[lead.currentSentiment] : 'Sem análise'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Sentiment history */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title text-base">Histórico de Sentimentos</h3>
                            {sentiments.length === 0 ? (
                                <div className="text-center py-8 opacity-50">
                                    <MdSentimentSatisfied size={40} className="mx-auto mb-2" />
                                    <p>Nenhuma análise de sentimento registrada</p>
                                </div>
                            ) : (
                                <div className="space-y-3 mt-2">
                                    {sentiments.map(s => (
                                        <div key={s.id} className="flex items-center justify-between p-3 bg-base-200 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <span className={`badge ${SENTIMENT_COLORS[s.sentiment]}`}>
                                                    {LEAD_SENTIMENT_LABELS[s.sentiment]}
                                                </span>
                                                {s.notes && (
                                                    <span className="text-sm opacity-70">{s.notes}</span>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-medium">
                                                    {Math.round(s.confidence * 100)}% confiança
                                                </div>
                                                <div className="text-xs opacity-50">
                                                    {format(new Date(s.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'chats' &&
                (!lead.chats || lead.chats.length === 0 ? (
                    <div className="text-center py-12 opacity-50">
                        <MdForum size={48} className="mx-auto mb-2" />
                        <p>Nenhuma conversa vinculada a este lead</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Mais de uma conversa (outro número, outro canal): escolhe qual abrir */}
                        {lead.chats.length > 1 && (
                            <div className="flex flex-wrap gap-2">
                                {lead.chats.map((chat: any) => {
                                    const active = (selectedChatId ?? lead.chats![0].id) === chat.id
                                    return (
                                        <button
                                            key={chat.id}
                                            className={`btn btn-sm ${active ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                                            onClick={() => setSelectedChatId(chat.id)}
                                        >
                                            <ChatChannelIcon channel={chat.channel} colored={!active} />
                                            {chat.currentDepartment?.name || chat.phoneNumber || `Conversa #${chat.id}`}
                                            <span className="text-xs opacity-60">
                                                {format(new Date(chat.updatedAt), 'dd/MM/yy', { locale: ptBR })}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                        <div className="h-[70vh] min-h-[480px] rounded-xl border border-base-300/60 overflow-hidden flex flex-col bg-base-100">
                            <AtendimentoChatPage
                                key={selectedChatId ?? lead.chats[0].id}
                                chatIdOverride={selectedChatId ?? lead.chats[0].id}
                                // Sem isto, a seta do chat navegava para a lista de atendimentos
                                onBack={() => setActiveTab('summary')}
                            />
                        </div>
                    </div>
                ))}

            {movePipelineDeal && (
                <MoveDealPipelineModal
                    dealIds={[movePipelineDeal.id]}
                    dealTitle={movePipelineDeal.title}
                    currentPipelineId={movePipelineDeal.pipelineId}
                    onClose={() => setMovePipelineDeal(null)}
                    onMoved={() => {
                        setMovePipelineDeal(null)
                        loadLead()
                    }}
                />
            )}

            {/* Edit Modal */}
            {showEditModal && (
                <dialog className="modal modal-open">
                    <div className="modal-box !max-w-3xl max-h-[85vh] overflow-y-auto">
                        <h3 className="font-bold text-lg mb-4">Editar Lead</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label"><span className="label-text">Nome</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    value={editForm.name || ''}
                                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Telefone</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    value={editForm.phoneNumber || ''}
                                    onChange={e => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">E-mail</span></label>
                                <input
                                    type="email"
                                    className="input input-bordered"
                                    value={editForm.email || ''}
                                    onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Origem</span></label>
                                <select
                                    className="select select-bordered"
                                    value={editForm.source || ''}
                                    onChange={e => setEditForm({ ...editForm, source: e.target.value as LeadSource })}
                                >
                                    {Object.entries(LEAD_SOURCE_LABELS).map(([val, label]) => (
                                        <option key={val} value={val}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Status</span></label>
                                <select
                                    className="select select-bordered"
                                    value={editForm.status || ''}
                                    onChange={e => setEditForm({ ...editForm, status: e.target.value as LeadStatus })}
                                >
                                    {Object.entries(LEAD_STATUS_LABELS).map(([val, label]) => (
                                        <option key={val} value={val}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Ciclo de Vida</span></label>
                                <select
                                    className="select select-bordered"
                                    value={editForm.lifecycleStatus || ''}
                                    onChange={e => setEditForm({ ...editForm, lifecycleStatus: e.target.value as LeadLifecycleStatus })}
                                >
                                    {Object.entries(LEAD_LIFECYCLE_LABELS).map(([val, label]) => (
                                        <option key={val} value={val}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Temperatura</span></label>
                                <select
                                    className="select select-bordered"
                                    value={editForm.temperature || ''}
                                    onChange={e => setEditForm({ ...editForm, temperature: e.target.value as LeadTemperature })}
                                >
                                    {Object.entries(LEAD_TEMPERATURE_LABELS).map(([val, label]) => (
                                        <option key={val} value={val}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Prazo Esperado</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="Ex: 3 meses"
                                    value={editForm.expectedTimeline || ''}
                                    onChange={e => setEditForm({ ...editForm, expectedTimeline: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Empresa</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    value={editForm.leadCompanyName || ''}
                                    onChange={e => setEditForm({ ...editForm, leadCompanyName: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Cargo</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    value={editForm.position || ''}
                                    onChange={e => setEditForm({ ...editForm, position: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Website</span></label>
                                <input
                                    type="url"
                                    className="input input-bordered"
                                    value={editForm.leadWebsite || ''}
                                    onChange={e => setEditForm({ ...editForm, leadWebsite: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">Tags (separadas por vírgula)</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    value={(editForm.tags || []).join(', ')}
                                    onChange={e =>
                                        setEditForm({
                                            ...editForm,
                                            tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean),
                                        })
                                    }
                                />
                            </div>
                            {/* MQL/SAL/SQL checkboxes */}
                            <div className="form-control col-span-full">
                                <label className="label"><span className="label-text">Qualificação MQL / SAL / SQL</span></label>
                                <div className="flex gap-6 flex-wrap">
                                    <label className="label cursor-pointer gap-2">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm checkbox-info"
                                            checked={editForm.isMQL || false}
                                            onChange={e => setEditForm({ ...editForm, isMQL: e.target.checked })}
                                        />
                                        <span className="label-text">MQL</span>
                                    </label>
                                    <label className="label cursor-pointer gap-2">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm checkbox-warning"
                                            checked={editForm.isSAL || false}
                                            onChange={e => setEditForm({ ...editForm, isSAL: e.target.checked })}
                                        />
                                        <span className="label-text">SAL</span>
                                    </label>
                                    <label className="label cursor-pointer gap-2">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm checkbox-success"
                                            checked={editForm.isSQL || false}
                                            onChange={e => setEditForm({ ...editForm, isSQL: e.target.checked })}
                                        />
                                        <span className="label-text">SQL</span>
                                    </label>
                                </div>
                            </div>
                            {/* BANT checkboxes */}
                            <div className="form-control col-span-full">
                                <label className="label"><span className="label-text">Qualificação BANT</span></label>
                                <div className="flex gap-6 flex-wrap">
                                    <label className="label cursor-pointer gap-2">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm"
                                            checked={editForm.hasBudget || false}
                                            onChange={e => setEditForm({ ...editForm, hasBudget: e.target.checked })}
                                        />
                                        <span className="label-text">Orçamento</span>
                                    </label>
                                    <label className="label cursor-pointer gap-2">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm"
                                            checked={editForm.isDecisionMaker || false}
                                            onChange={e => setEditForm({ ...editForm, isDecisionMaker: e.target.checked })}
                                        />
                                        <span className="label-text">Decisor</span>
                                    </label>
                                    <label className="label cursor-pointer gap-2">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm"
                                            checked={editForm.hasNeed || false}
                                            onChange={e => setEditForm({ ...editForm, hasNeed: e.target.checked })}
                                        />
                                        <span className="label-text">Necessidade</span>
                                    </label>
                                </div>
                            </div>
                            {/* UTM fields */}
                            <div className="form-control">
                                <label className="label"><span className="label-text">UTM Source</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm"
                                    value={editForm.utmSource || ''}
                                    onChange={e => setEditForm({ ...editForm, utmSource: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">UTM Medium</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm"
                                    value={editForm.utmMedium || ''}
                                    onChange={e => setEditForm({ ...editForm, utmMedium: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">UTM Campaign</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm"
                                    value={editForm.utmCampaign || ''}
                                    onChange={e => setEditForm({ ...editForm, utmCampaign: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">UTM Term</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm"
                                    value={editForm.utmTerm || ''}
                                    onChange={e => setEditForm({ ...editForm, utmTerm: e.target.value })}
                                />
                            </div>
                            <div className="form-control col-span-full">
                                <label className="label"><span className="label-text">Observações</span></label>
                                <textarea
                                    className="textarea textarea-bordered"
                                    rows={3}
                                    value={editForm.notes || ''}
                                    onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="modal-action">
                            <button className="btn btn-ghost" onClick={() => setShowEditModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleSaveEdit}
                                disabled={saving}
                            >
                                {saving && <span className="loading loading-spinner loading-sm" />}
                                Salvar
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setShowEditModal(false)}>close</button>
                    </form>
                </dialog>
            )}

            {/* Convert Modal */}
            {showConvertModal && (
                <dialog className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">Converter em Cliente</h3>
                        <p className="text-sm opacity-70 mb-4">
                            O lead será convertido em cliente. Preencha os dados adicionais se necessário.
                        </p>
                        <div className="space-y-4">
                            <div className="form-control">
                                <label className="label"><span className="label-text">Nome</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    value={convertForm.name || ''}
                                    onChange={e => setConvertForm({ ...convertForm, name: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">E-mail</span></label>
                                <input
                                    type="email"
                                    className="input input-bordered"
                                    value={convertForm.email || ''}
                                    onChange={e => setConvertForm({ ...convertForm, email: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label"><span className="label-text">CPF</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="000.000.000-00"
                                    value={convertForm.cpf || ''}
                                    onChange={e => setConvertForm({ ...convertForm, cpf: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="modal-action">
                            <button className="btn btn-ghost" onClick={() => setShowConvertModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={handleConvert}
                                disabled={converting}
                            >
                                {converting && <span className="loading loading-spinner loading-sm" />}
                                Converter
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setShowConvertModal(false)}>close</button>
                    </form>
                </dialog>
            )}

            {/* Assign Modal */}
            {showAssignModal && (
                <dialog className="modal modal-open">
                    <div className="modal-box !max-w-md">
                        <h3 className="font-bold text-lg mb-4">Atribuir Lead</h3>
                        {/* Só o responsável: a carteira pertence ao cliente e
                            é definida na conversão do lead. */}
                        <div className="space-y-4">
                            <UserSelector
                                value={assignForm.assignedUserId ?? null}
                                onChange={userId =>
                                    setAssignForm({
                                        ...assignForm,
                                        assignedUserId: userId ?? undefined,
                                    })
                                }
                                label="Responsável"
                                placeholder="Buscar usuário..."
                                scope="organization"
                            />
                        </div>
                        <div className="modal-action">
                            <button className="btn btn-ghost" onClick={() => setShowAssignModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleAssign}
                                disabled={assigning}
                            >
                                {assigning && <span className="loading loading-spinner loading-sm" />}
                                Atribuir
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setShowAssignModal(false)}>close</button>
                    </form>
                </dialog>
            )}
        </div>
    )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm opacity-70">{label}</span>
            <span className="text-sm font-medium text-right max-w-[60%] break-words">{value}</span>
        </div>
    )
}

function BANTItem({ label, checked }: { label: string; checked: boolean }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm opacity-70">{label}</span>
            {checked ? (
                <span className="badge badge-success badge-sm gap-1">
                    <MdCheckCircle size={12} /> Sim
                </span>
            ) : (
                <span className="badge badge-ghost badge-sm gap-1">
                    <MdCancel size={12} /> Não
                </span>
            )}
        </div>
    )
}
